import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpLink } from 'apollo-angular/http';
import { ApolloLink } from '@apollo/client/core';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import extractFiles from 'extract-files/extractFiles.mjs';
import isExtractableFile from 'extract-files/isExtractableFile.mjs';
import { environment } from '../../../environments/environment';
import authLink from './auth-link';
import errorLink, { setDashboardNavigator } from './error-link';
import cache from './cache';
import { readStoredToken } from '../auth/auth-token';
import { WsConnectionService, WsConnectionState } from '../services/ws-connection.service';

interface Definition {
  kind: string;
  operation?: string;
}

function extractFilesWrapper(body: unknown) {
  return extractFiles(body, isExtractableFile);
}

export function apolloOptionsFactory() {
    const router   = inject(Router);
  setDashboardNavigator(() => { router.navigate(['/dashboard']); });
  const httpLink = inject(HttpLink);
  const wsSvc    = inject(WsConnectionService);

  let hasConnectedBefore = false;

  const wsLink = new GraphQLWsLink(
    createClient({
      url: environment.graphql_WS_URI,
      connectionParams: () => {
        const token = readStoredToken();
        return { Authorization: token ? `Bearer ${token}` : '' };
      },
      retryAttempts: Infinity,
      keepAlive: 10000,
      on: {
        connecting: () => {
          const isReconnect = hasConnectedBefore;
          wsSvc.onConnectionStatusChange(
            isReconnect ? WsConnectionState.RECONNECTING : WsConnectionState.CONNECTING,
            isReconnect,
          );
        },
        connected: () => {
          const isReconnect = hasConnectedBefore;
          hasConnectedBefore = true;
          wsSvc.onConnectionStatusChange(WsConnectionState.CONNECTED, isReconnect);
        },
        closed: () => {
          wsSvc.onConnectionStatusChange(WsConnectionState.CLOSED, false);
        },
        error: () => {
          wsSvc.onConnectionStatusChange(WsConnectionState.ERROR, false);
        },
      },
    })
  );

  const httpChain = ApolloLink.from([
    authLink,
    errorLink,
    httpLink.create({ uri: environment.graphqlURI, extractFiles: extractFilesWrapper as never }),
  ]);

  const link = ApolloLink.split(
    ({ query }) => {
      const { kind, operation }: Definition = getMainDefinition(query);
      return kind === 'OperationDefinition' && operation === 'subscription';
    },
    wsLink,
    httpChain,
  );

  return {
    link,
    cache,
    defaultOptions: {
      watchQuery: { fetchPolicy: 'no-cache' as const, errorPolicy: 'all' as const },
      query:       { fetchPolicy: 'no-cache' as const, errorPolicy: 'all' as const },
    },
  };
}
